import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Form } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

import Modal from 'components/Modal';
import Button from 'components/Button';
import FormInput from 'components/FormInput';
import FormPasswordInput from 'components/FormPasswordInput';
import FormOTP from 'components/FormOTP';
import { useAccountSettingsContext } from 'layouts/AccountSettingsLayout/contexts/AccountSettingsContext';
import { useAppContext } from 'contexts/AppContext';
import { replaceAuthToken } from 'utils/common';
import { toastStyle } from 'utils/helper';

const STEP = {
  REQUEST: 'request',
  AWAITING_OTP: 'awaiting_otp',
};

const extractServerMessage = error => {
  const data = error?.response?.data;
  const msg = data?.message;
  if (typeof msg === 'string') return msg;
  if (msg && typeof msg === 'object') {
    const firstKey = Object.keys(msg)[0];
    const firstVal = firstKey ? msg[firstKey] : null;
    if (Array.isArray(firstVal)) return firstVal[0];
    if (typeof firstVal === 'string') return firstVal;
  }
  return data?.detail || null;
};

const ChangeEmailModal = ({ show, onHide }) => {
  const [step, setStep] = useState(STEP.REQUEST);
  const [otpValue, setOtpValue] = useState('');
  const [otpError, setOtpError] = useState('');
  const [submittedEmail, setSubmittedEmail] = useState('');

  const { requestEmailChangeMutation, confirmEmailChangeMutation } =
    useAccountSettingsContext();
  const { refetchProfile } = useAppContext();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
  } = useForm({
    mode: 'onChange',
    defaultValues: { password: '', new_email: '' },
  });

  const closeAndReset = () => {
    setStep(STEP.REQUEST);
    setOtpValue('');
    setOtpError('');
    setSubmittedEmail('');
    reset();
    onHide();
  };

  const handleRequest = handleSubmit(async data => {
    try {
      await requestEmailChangeMutation.mutateAsync(data);
      setSubmittedEmail(data.new_email);
      setStep(STEP.AWAITING_OTP);
      toast.success('OTP sent to the new email address.', toastStyle);
    } catch (error) {
      const message = extractServerMessage(error);
      toast.error(
        message || 'Could not request email change. Please try again later.',
        toastStyle
      );
    }
  });

  const handleResendOtp = () => {
    // Go back to step 1; the user re-enters the password and triggers a new OTP.
    setOtpValue('');
    setOtpError('');
    setStep(STEP.REQUEST);
  };

  const handleConfirm = async () => {
    setOtpError('');
    if (!otpValue || String(otpValue).length !== 4) {
      setOtpError('Please enter the 4-digit code.');
      return;
    }
    try {
      const res = await confirmEmailChangeMutation.mutateAsync({
        otp: String(otpValue),
      });
      const newToken = res?.data?.data?.token;
      if (newToken) {
        replaceAuthToken(newToken);
      }
      toast.success('Email updated successfully.', toastStyle);
      refetchProfile?.();
      closeAndReset();
    } catch (error) {
      const message = extractServerMessage(error);
      setOtpError(message || 'Invalid or expired code.');
    }
  };

  const requestPending = requestEmailChangeMutation?.isLoading;
  const confirmPending = confirmEmailChangeMutation?.isLoading;

  return (
    <Modal
      show={show}
      onHide={closeAndReset}
      className="change-email-modal"
      size="md"
      centered
    >
      <div className="change-email-modal__body">
        <h4 className="change-email-modal__title">Change Email</h4>

        {step === STEP.REQUEST && (
          <Form onSubmit={handleRequest}>
            <p className="change-email-modal__description">
              Enter your password and the new email address. We&apos;ll send a
              4-digit code to the new address to confirm.
            </p>
            <FormInput
              label="New Email"
              type="email"
              placeholder="new@example.com"
              error={errors?.new_email?.message}
              {...register('new_email', {
                required: 'New email is required',
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: 'Please enter a valid email address',
                },
              })}
            />
            <FormPasswordInput
              label="Current Password"
              placeholder="Enter your current password"
              error={errors?.password?.message}
              {...register('password', {
                required: 'Current password is required',
              })}
            />
            <div className="change-email-modal__actions">
              <Button
                label="Cancel"
                type="reset"
                onClick={closeAndReset}
                variant="grey-outlined"
              />
              <Button
                label={requestPending ? 'Sending...' : 'Send Code'}
                type="submit"
                variant="primary"
                disabled={!isValid || requestPending}
              />
            </div>
          </Form>
        )}

        {step === STEP.AWAITING_OTP && (
          <>
            <p className="change-email-modal__description">
              Enter the 4-digit code sent to <strong>{submittedEmail}</strong>.
            </p>
            <FormOTP
              label="Verification Code"
              otp={otpValue}
              onChange={value => {
                setOtpValue(value);
                setOtpError('');
              }}
              error={otpError}
              handleResendOtp={handleResendOtp}
            />
            <div className="change-email-modal__actions">
              <Button
                label="Cancel"
                type="button"
                onClick={closeAndReset}
                variant="grey-outlined"
              />
              <Button
                label={confirmPending ? 'Confirming...' : 'Confirm'}
                type="button"
                onClick={handleConfirm}
                variant="primary"
                disabled={
                  confirmPending || !otpValue || String(otpValue).length !== 4
                }
              />
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

ChangeEmailModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
};

export default ChangeEmailModal;
